/**
 * @easycancha/ui-components
 *
 * Componentes UI reutilizables para AngularJS
 *
 * @example
 * ```typescript
 * import angular from 'angular';
 * import { MenuModule, CardModule, BannerModule } from '@easycancha/ui-components';
 *
 * angular.module('myApp', [MenuModule, CardModule, BannerModule]);
 * ```
 */

// =============================================================================
// MENU COMPONENT
// =============================================================================

export {
  MODULE_NAME as MenuModule,
  MenuController,
  MenuItemController,
  menuComponentDefinition,
  menuDropdownComponentDefinition,
  menuNavbarComponentDefinition,
  menuItemComponentDefinition,
  MENU_TEMPLATE,
  MENU_DROPDOWN_TEMPLATE,
  MENU_NAVBAR_TEMPLATE,
  VARIANTS as MENU_VARIANTS,
  SIZES as MENU_SIZES,
  ICONS as MENU_ICONS,
  DEFAULTS as MENU_DEFAULTS
} from './components/Menu';

export type {
  MenuItem,
  MenuVariant,
  MenuSize,
  MenuOrientation,
  MenuIconType,
  VariantStyles as MenuVariantStyles,
  SizeStyles as MenuSizeStyles,
  IMenuComponentBindings
} from './components/Menu';

// =============================================================================
// CARD COMPONENT
// =============================================================================

export {
  MODULE_NAME as CardModule,
  CardController,
  CardMediaController,
  CardContentController,
  cardComponentDefinition,
  cardCompactComponentDefinition,
  cardMediaComponentDefinition,
  cardContentComponentDefinition,
  cardTitleComponentDefinition,
  cardSubtitleComponentDefinition,
  cardTextComponentDefinition,
  cardCaptionComponentDefinition,
  CARD_TEMPLATE,
  CARD_COMPACT_TEMPLATE,
  VARIANTS as CARD_VARIANTS,
  SIZES as CARD_SIZES,
  DEFAULTS as CARD_DEFAULTS,
  BINDING_NAMES as CARD_BINDING_NAMES
} from './components/Card';

export type {
  CardVariant,
  CardSize,
  CardLayout,
  VariantStyles as CardVariantStyles,
  SizeStyles as CardSizeStyles,
  ICardComponentBindings
} from './components/Card';

// =============================================================================
// BANNER COMPONENT
// =============================================================================

export {
  MODULE_NAME as BannerModule,
  BannerController,
  BannerTitleController,
  BannerTextController,
  bannerComponentDefinition,
  bannerVerticalComponentDefinition,
  bannerCompactComponentDefinition,
  bannerTitleComponentDefinition,
  bannerTextComponentDefinition,
  bannerActionsComponentDefinition,
  BANNER_TEMPLATE,
  BANNER_VERTICAL_TEMPLATE,
  BANNER_COMPACT_TEMPLATE,
  BANNER_TITLE_TEMPLATE,
  BANNER_TEXT_TEMPLATE,
  BANNER_ACTIONS_TEMPLATE,
  VARIANTS as BANNER_VARIANTS,
  SIZES as BANNER_SIZES,
  ICONS as BANNER_ICONS,
  DEFAULTS as BANNER_DEFAULTS
} from './components/Banner';

export type {
  BannerVariant,
  BannerSize,
  BannerIconType,
  IconPosition,
  VariantStyles as BannerVariantStyles,
  SizeStyles as BannerSizeStyles,
  IBannerComponentBindings
} from './components/Banner';

// =============================================================================
// BUTTON COMPONENT
// =============================================================================

export {
  MODULE_NAME as ButtonModule,
  ButtonController,
  buttonComponentDefinition,
  BUTTON_TEMPLATE,
  INTENT_EMPHASIS as BUTTON_INTENT_EMPHASIS,
  SIZES as BUTTON_SIZES,
  ICONS as BUTTON_ICONS,
  DEFAULTS as BUTTON_DEFAULTS
} from './components/Button';

export type {
  ButtonIntent,
  ButtonEmphasis,
  ButtonSize,
  ButtonShape,
  ButtonType,
  ButtonIconType,
  IButtonComponentBindings
} from './components/Button';

// =============================================================================
// ALERT COMPONENT
// =============================================================================

export {
  MODULE_NAME as AlertModule,
  AlertController,
  alertComponentDefinition,
  ALERT_TEMPLATE,
  THEME_CLASSES as ALERT_THEME_CLASSES,
  ICONS as ALERT_ICONS,
  DEFAULTS as ALERT_DEFAULTS
} from './components/Alert';

export type {
  AlertTheme,
  AlertIconType,
  IAlertComponentBindings
} from './components/Alert';

// =============================================================================
// SNACKBAR COMPONENT
// =============================================================================

export {
  MODULE_NAME as SnackbarModule,
  SnackbarController,
  snackbarComponentDefinition,
  SNACKBAR_TEMPLATE,
  MODE_CLASSES as SNACKBAR_MODE_CLASSES,
  DEFAULTS as SNACKBAR_DEFAULTS
} from './components/Snackbar';

export type {
  SnackbarMode,
  ISnackbarComponentBindings
} from './components/Snackbar';

// =============================================================================
// SEGMENTED CONTROL COMPONENT
// =============================================================================

export {
  MODULE_NAME as SegmentedControlModule,
  SegmentedControlController,
  segmentedControlComponentDefinition,
  SEGMENTED_CONTROL_TEMPLATE,
  DEFAULTS as SEGMENTED_CONTROL_DEFAULTS
} from './components/SegmentedControl';

export type {
  SegmentedControlOption,
  ISegmentedControlComponentBindings
} from './components/SegmentedControl';

// =============================================================================
// TABS COMPONENT
// =============================================================================

export {
  MODULE_NAME as TabsModule,
  TabsController,
  tabsComponentDefinition,
  TABS_TEMPLATE,
  BASE_CLASSES as TABS_BASE_CLASSES
} from './components/Tabs';

export type {
  TabsOption,
  ITabsComponentBindings
} from './components/Tabs';

// =============================================================================
// CHIP COMPONENT
// =============================================================================

export {
  MODULE_NAME as ChipModule,
  ChipController,
  chipComponentDefinition,
  CHIP_TEMPLATE,
  INTENT_EMPHASIS as CHIP_INTENT_EMPHASIS,
  SIZES as CHIP_SIZES,
  ICONS as CHIP_ICONS,
  DEFAULTS as CHIP_DEFAULTS
} from './components/Chip';

export type {
  ChipIntent,
  ChipEmphasis,
  ChipSize,
  ChipIconType,
  IChipComponentBindings
} from './components/Chip';

// =============================================================================
// FAB COMPONENT
// =============================================================================

export {
  MODULE_NAME as FabModule,
  FabController,
  fabComponentDefinition,
  FAB_TEMPLATE,
  INTENT_EMPHASIS as FAB_INTENT_EMPHASIS,
  SIZES as FAB_SIZES,
  ICONS as FAB_ICONS,
  DEFAULTS as FAB_DEFAULTS
} from './components/Fab';

export type {
  FabIntent,
  FabEmphasis,
  FabSize,
  FabType,
  FabIconType,
  IFabComponentBindings
} from './components/Fab';

// =============================================================================
// INPUT TEXT COMPONENT
// =============================================================================

export {
  MODULE_NAME as InputTextModule,
  InputTextController,
  inputTextComponentDefinition,
  INPUT_TEXT_TEMPLATE,
  SIZES as INPUT_TEXT_SIZES,
  ICONS as INPUT_TEXT_ICONS,
  DEFAULTS as INPUT_TEXT_DEFAULTS
} from './components/InputText';

export type {
  InputTextLabelPosition,
  InputTextSize,
  InputTextIconType,
  IInputTextComponentBindings
} from './components/InputText';

// =============================================================================
// INPUT PASSWORD COMPONENT
// =============================================================================

export {
  MODULE_NAME as InputPasswordModule,
  InputPasswordController,
  inputPasswordComponentDefinition,
  INPUT_PASSWORD_TEMPLATE,
  SIZES as INPUT_PASSWORD_SIZES,
  ICONS as INPUT_PASSWORD_ICONS,
  DEFAULTS as INPUT_PASSWORD_DEFAULTS
} from './components/InputPassword';

export type {
  InputPasswordSize,
  IInputPasswordComponentBindings
} from './components/InputPassword';

// =============================================================================
// CHECKBOX COMPONENT
// =============================================================================

export {
  MODULE_NAME as CheckboxModule,
  CheckboxController,
  checkboxComponentDefinition,
  CHECKBOX_TEMPLATE,
  SIZES as CHECKBOX_SIZES,
  DEFAULTS as CHECKBOX_DEFAULTS
} from './components/Checkbox';

export type { CheckboxSize, ICheckboxComponentBindings } from './components/Checkbox';

// =============================================================================
// SWITCH COMPONENT
// =============================================================================

export {
  MODULE_NAME as SwitchModule,
  SwitchController,
  switchComponentDefinition,
  SWITCH_TEMPLATE,
  SIZES as SWITCH_SIZES,
  DEFAULTS as SWITCH_DEFAULTS
} from './components/Switch';

export type { SwitchSize, ISwitchComponentBindings } from './components/Switch';

// =============================================================================
// STEPPER COMPONENT
// =============================================================================

export {
  MODULE_NAME as StepperModule,
  StepperController,
  stepperComponentDefinition,
  STEPPER_TEMPLATE,
  SIZES as STEPPER_SIZES,
  DEFAULTS as STEPPER_DEFAULTS
} from './components/Stepper';

export type { StepperSize, IStepperComponentBindings } from './components/Stepper';

// =============================================================================
// SELECT COMPONENT
// =============================================================================

export {
  MODULE_NAME as SelectModule,
  SelectController,
  SelectOptionController,
  selectComponentDefinition,
  selectOptionComponentDefinition,
  SELECT_TEMPLATE,
  SIZES as SELECT_SIZES,
  DEFAULTS as SELECT_DEFAULTS
} from './components/Select';

export type { SelectOption, SelectSize, ISelectComponentBindings } from './components/Select';

// =============================================================================
// INPUT SEARCH COMPONENT
// =============================================================================

export {
  MODULE_NAME as InputSearchModule,
  InputSearchController,
  inputSearchComponentDefinition,
  INPUT_SEARCH_TEMPLATE,
  SIZES as INPUT_SEARCH_SIZES,
  ICONS as INPUT_SEARCH_ICONS,
  DEFAULTS as INPUT_SEARCH_DEFAULTS
} from './components/InputSearch';

export type { InputSearchSize, IInputSearchComponentBindings } from './components/InputSearch';

// =============================================================================
// DATE PICKER COMPONENT
// =============================================================================

export {
  MODULE_NAME as DatePickerModule,
  DatePickerController,
  datePickerComponentDefinition,
  DATE_PICKER_TEMPLATE,
  SIZES as DATE_PICKER_SIZES,
  DEFAULTS as DATE_PICKER_DEFAULTS
} from './components/DatePicker';

export type { DatePickerSize, IDatePickerComponentBindings } from './components/DatePicker';

// =============================================================================
// LOGIN VIEW
// =============================================================================

export {
  MODULE_NAME as LoginViewModule,
  LoginController,
  loginComponentDefinition,
  LOGIN_TEMPLATE,
  DEFAULTS as LOGIN_DEFAULTS,
  APPLE_LOGO as LOGIN_APPLE_LOGO,
  GOOGLE_LOGO as LOGIN_GOOGLE_LOGO,
  BASE_CLASSES as LOGIN_BASE_CLASSES
} from './views/Login';

export type { LoginTexts, ILoginComponentBindings } from './views/Login';

// =============================================================================
// ICON COMPONENT
// =============================================================================

export {
  MODULE_NAME as IconModule,
  IconController,
  iconComponentDefinition,
  ICON_TEMPLATE,
  SIZE_CLASSES as ICON_SIZE_CLASSES,
  VALID_SIZES as ICON_VALID_SIZES,
  DEFAULTS as ICON_DEFAULTS
} from './components/Icon';

export type { IconSize, IEcIconComponentBindings } from './components/Icon';

// =============================================================================
// ICON REGISTRY
// =============================================================================

export { getIcon, ICON_REGISTRY, LEGACY_ICONS } from './icons';

export type { IconVariant, IconName } from './icons';

// =============================================================================
// THEME
// =============================================================================

export { EcThemeService, EC_THEME_MODULE } from './_base/theme.service';
export type { EcTheme } from './_base/theme.service';

// =============================================================================
// UTILITIES
// =============================================================================

export {
  BaseController,
  createComponent,
  createStoryConfig,
  createStoryTemplate
} from './_base/component-factory';

export { hasTranscludedContent } from './_base/transclusion';

export type {
  ComponentConfig,
  ComponentResult,
  StoryConfigOptions,
  StoryTemplateOptions
} from './_base/component-factory';

// =============================================================================
// ALL MODULES (convenience export)
// =============================================================================

import { MODULE_NAME as MenuModuleName } from './components/Menu';
import { MODULE_NAME as CardModuleName } from './components/Card';
import { MODULE_NAME as BannerModuleName } from './components/Banner';
import { MODULE_NAME as AlertModuleName } from './components/Alert';
import { MODULE_NAME as ButtonModuleName } from './components/Button';
import { MODULE_NAME as ChipModuleName } from './components/Chip';
import { MODULE_NAME as SnackbarModuleName } from './components/Snackbar';
import { MODULE_NAME as SegmentedControlModuleName } from './components/SegmentedControl';
import { MODULE_NAME as TabsModuleName } from './components/Tabs';
import { MODULE_NAME as FabModuleName } from './components/Fab';
import { MODULE_NAME as InputTextModuleName } from './components/InputText';
import { MODULE_NAME as InputPasswordModuleName } from './components/InputPassword';
import { MODULE_NAME as CheckboxModuleName } from './components/Checkbox';
import { MODULE_NAME as SwitchModuleName } from './components/Switch';
import { MODULE_NAME as StepperModuleName } from './components/Stepper';
import { MODULE_NAME as SelectModuleName } from './components/Select';
import { MODULE_NAME as InputSearchModuleName } from './components/InputSearch';
import { MODULE_NAME as DatePickerModuleName } from './components/DatePicker';
import { MODULE_NAME as IconModuleName } from './components/Icon';
import { MODULE_NAME as LoginViewModuleName } from './views/Login';
import { EC_THEME_MODULE } from './_base/theme.service';

/**
 * Array of all component module names for easy registration
 *
 * @example
 * ```typescript
 * import angular from 'angular';
 * import { ALL_MODULES } from '@easycancha/ui-components';
 *
 * angular.module('myApp', ALL_MODULES);
 * ```
 */
export const ALL_MODULES: string[] = [
  EC_THEME_MODULE,
  MenuModuleName,
  CardModuleName,
  BannerModuleName,
  AlertModuleName,
  ButtonModuleName,
  ChipModuleName,
  SnackbarModuleName,
  SegmentedControlModuleName,
  TabsModuleName,
  FabModuleName,
  InputTextModuleName,
  InputPasswordModuleName,
  CheckboxModuleName,
  SwitchModuleName,
  StepperModuleName,
  SelectModuleName,
  InputSearchModuleName,
  DatePickerModuleName,
  IconModuleName,
  LoginViewModuleName
];

/**
 * Default export with all modules
 */
export default {
  MenuModule: MenuModuleName,
  CardModule: CardModuleName,
  BannerModule: BannerModuleName,
  AlertModule: AlertModuleName,
  ButtonModule: ButtonModuleName,
  ChipModule: ChipModuleName,
  SnackbarModule: SnackbarModuleName,
  SegmentedControlModule: SegmentedControlModuleName,
  TabsModule: TabsModuleName,
  FabModule: FabModuleName,
  InputTextModule: InputTextModuleName,
  InputPasswordModule: InputPasswordModuleName,
  CheckboxModule: CheckboxModuleName,
  SwitchModule: SwitchModuleName,
  StepperModule: StepperModuleName,
  SelectModule: SelectModuleName,
  InputSearchModule: InputSearchModuleName,
  DatePickerModule: DatePickerModuleName,
  IconModule: IconModuleName,
  LoginViewModule: LoginViewModuleName,
  ALL_MODULES
};
